import React, { useEffect, useState } from 'react';
import HeaderCard from '../../components/Header/HeaderCard';
import { apiService } from '../../components/Config/api.service';
import TableList from '../../components/Tools/TableList';
import { RiBuilding2Fill, RiFileList3Fill, RiPriceTag3Fill } from "react-icons/ri";
import {notify} from '../../components/Utils/Notifications';
import CreatableSelect from 'react-select/creatable';
import Drawer from '../../components/Tools/DrawerCustom';
import CategoryManager from './CategoryManager';
import DepartmentManager from './DepartmentManager';
import AssignmentManager from './AssignmentManager';
import { TAG_COLORS } from '../../components/Constantes/Colors';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaArchive } from "react-icons/fa";
import Modal from '../../components/Utils/ModalShow';

function Employees() {
    const [data, setData] = useState([]);
    const [sortBy, setSortBy] = useState({ key: 'name', direction: 'asc' });
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 25,
        total_pages: 1,
        total_count: 0,
        page: 1,
    });

    const [selectedIds, setSelectedIds] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showCategoryDrawer, setShowCategoryDrawer] = useState(false);
    const [showDepartmentDrawer, setShowDepartmentDrawer] = useState(false);
    const [showAssignmentDrawer, setShowAssignmentDrawer] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [showArchiveConfirmModal, setShowArchiveConfirmModal] = useState(false);
    const [isArchiving, setIsArchiving] = useState(true);

    // New states for bulk department/assignment
    const [departments, setDepartments] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [showDeptModal, setShowDeptModal] = useState(false);

    const [assignments, setAssignments] = useState([]);
    const [assignmentOptions, setAssignmentOptions] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);

    const [categoryOptions, setCategoryOptions] = useState([]);
    const handleCreateCategory = async (inputValue) => {
        try {
            const res = await apiService.saveElement('/employee/category', { name: inputValue });
            const newCat = res.data.result;
            const newOption = { value: newCat.id, label: newCat.name };
            setCategories(prev => [...prev, newCat]);
            setSelectedCategory(newOption);
        } catch (e) {
            notify("Erreur lors de la création de l'étiquette", "", 3);
        }
    };

    // New: creatable handlers for departments & assignments (same pattern as categories)
    const handleCreateDepartment = async (inputValue) => {
        try {
            const res = await apiService.saveElement('/employee/department', { name: inputValue });
            const newDept = res.data.result || res.data; // depending on API shape
            const newOption = { value: newDept.id, label: newDept.name };
            setDepartments(prev => [...prev, newDept]);
            setDepartmentOptions(prev => [...prev, newOption]);
            setSelectedDepartment(newOption);
            notify("Département créé et sélectionné", "", 2);
        } catch (e) {
            notify("Erreur lors de la création du département", "", 3);
            console.error(e);
        }
    };

    const handleCreateAssignment = async (inputValue) => {
        try {
            const res = await apiService.saveElement('/employee/assignment', { name: inputValue });
            const newAssign = res.data.result || res.data;
            const newOption = { value: newAssign.id, label: newAssign.name };
            setAssignments(prev => [...prev, newAssign]);
            setAssignmentOptions(prev => [...prev, newOption]);
            setSelectedAssignment(newOption);
            notify("Assignation créée et sélectionnée", "", 2);
        } catch (e) {
            notify("Erreur lors de la création de l'assignation", "", 3);
            console.error(e);
        }
    };

    const handleAffectCategory = async () => {
        if (!selectedCategory || selectedIds.length === 0) return;
        try {
            await apiService.saveElement('/employees/assign-category', {
                employee_ids: selectedIds,
                category_id: selectedCategory.value,
            });
            notify("Catégorie affectée avec succès", "", 2);
            fetchEmployees(pagination.page, searchTerm, filterBy);
            setShowModal(false);
            setSelectedCategory('');
        } catch (e) {
            notify("Erreur lors de l'affectation de la catégorie", "", 3);
            console.error("Erreur lors de l'affectation de la catégorie", e);
        }
    };

    // New: assign department to many employees
    const handleAffectDepartment = async () => {
        if (!selectedDepartment || selectedIds.length === 0) return;
        try {
            await apiService.saveElement('/employees/assign-department', {
                employee_ids: selectedIds,
                department_id: selectedDepartment.value,
            });
            notify("Département affecté avec succès", "", 2);
            fetchEmployees(pagination.page, searchTerm, filterBy);
            setShowDeptModal(false);
            setSelectedDepartment(null);
            setSelectedIds([]);
        } catch (e) {
            notify("Erreur lors de l'affectation du département", "", 3);
            console.error("Erreur lors de l'affectation du département", e);
        }
    };

    // New: assign assignment to many employees
    const handleAffectAssignment = async () => {
        if (!selectedAssignment || selectedIds.length === 0) return;
        try {
            await apiService.saveElement('/employees/assign-assignment', {
                employee_ids: selectedIds,
                assignment_id: selectedAssignment.value,
            });
            notify("Affectation affectée avec succès", "", 2);
            fetchEmployees(pagination.page, searchTerm, filterBy);
            setShowAssignmentModal(false);
            setSelectedAssignment(null);
            setSelectedIds([]);
        } catch (e) {
            notify("Erreur lors de l'affectation", "", 3);
            console.error("Erreur lors de l'affectation", e);
        }
    };

    const handleArchive = async (archive = true) => {
        if (selectedIds.length === 0) return;
        setIsArchiving(archive);
        setShowArchiveConfirmModal(true);
    };

    const confirmArchive = async () => {
        try {
            await apiService.saveElement('/employees/archive', {
                employee_ids: selectedIds,
                archive: isArchiving,
            });
            const action = isArchiving ? "archivé(s)" : "désarchivé(s)";
            notify(`Employé(s) ${action} avec succès`, "", 2);
            fetchEmployees(pagination.page, searchTerm, filterBy);
            setSelectedIds([]);
        } catch (e) {
            const action = isArchiving ? "l'archivage" : "la désarchivage";
            notify(`Erreur lors de ${action}`, "", 3);
            console.error(`Erreur lors de ${action}`, e);
        } finally {
            setShowArchiveConfirmModal(false);
        }
    };

    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    
    const navigate = useNavigate();

    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    const searchFromUrl = searchParams.get('search') || '';
    const filterFromUrl = searchParams.get('filter_by') ? JSON.parse(searchParams.get('filter_by')) : '';

    const [searchTerm, setSearchTerm] = useState(searchFromUrl);
    const [filterBy, setFilterBy] = useState(filterFromUrl);

    const [showDepartmentColumn, setShowDepartmentColumn] = useState(true);
    const [showAssignmentColumn, setShowAssignmentColumn] = useState(true);

    const columns = [
        { key: 'name', label: 'Name', sortable: true, avatar: true},
        { key: 'email', label: 'Email' },
        {
            key: 'poste',
            label: 'Poste',
            render: (value) => (
                <span className="badge bg-[--bg-color]  px-2 py-1 rounded-full">
                    {value.poste || "Aucun Poste"}
                </span>
            )
        },
        { key: 'manager', label: 'Manager' },
        // department column controlled by manager
        ...(showDepartmentColumn ? [{
            key: 'department', label: 'Département',
            render: (value) => (
                <span className="badge bg-[--bg-color]  px-2 py-1 rounded-full">
                    {value.department ? value.department.name : "Aucun Département"}
                </span>
            )
        }] : []),
        // assignment column controlled by manager
        ...(showAssignmentColumn ? [{
            key: 'assignment', label: 'Affectation',
            render: (value) => (
                <span className="badge bg-[--bg-color]  px-2 py-1 rounded-full">
                    {value.assignment ? value.assignment.name : "Aucune Affectation"}
                </span>
            )
        }] : []),
        {
            key: 'categories', 
            label: 'Etiquettes',
            render: (value) => {
                const cats = value.categories || [];
                const displayCats = cats.slice(0, 3);
                return (
                    <div className="flex flex-wrap gap-1">
                        {displayCats.map(cat => (
                            <span 
                                key={cat.id} 
                                className="badge rounded-full  px-2 py-1 bg-[--bg-color]"
                                style={{
                                    color: TAG_COLORS[(cat.color || 1) - 1],                                    
                                }}
                            >
                                {cat.name}
                            </span>
                        ))}
                        {cats.length > 3 && (
                            <span className="px-2 py-1 badge rounded bg-[--bg-color] text-[--color-primary]">
                                ...
                            </span>
                        )}
                    </div>
                );
            }
        },
    ]


    const fetchEmployees = (page = 1, search = '', filter = '', sort = sortBy) => {
        setLoading(true);
    
        const offset = (page - 1) * pagination.limit;
    
        const params = new URLSearchParams();
        params.append('offset', offset);
        params.append('limit', pagination.limit);        
        params.append('archived', showArchived );        
        if (search) {
            params.append('search', search);
        }
        if (filter) {
            params.append('filter_by', JSON.stringify(filter));
        }
        if (sort && sort.key) {
            params.append('sort_by', sort.key);
            params.append('sort_dir', sort.direction);
        }
    
        apiService.getElement(`/employees?${params.toString()}`)
            .then(response => {
                setData(response.data.employees);
                const responseData = response.data;
                if (responseData.pagination) {
                    const currentPage = Math.floor(responseData.pagination.offset / responseData.pagination.limit) + 1;
                    setPagination({
                        ...responseData.pagination,
                        page: currentPage,
                    });
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
                setLoading(false);
            });
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortBy.key === key && sortBy.direction === 'asc') {
            direction = 'desc';
        }
        const newSort = { key, direction };
        setSortBy(newSort);
        fetchEmployees(pagination.page, searchTerm, filterBy, newSort);
    };

    useEffect(() => {
        fetchEmployees(pageFromUrl, searchFromUrl, filterFromUrl);
        // eslint-disable-next-line
    }, [searchParams]);

    useEffect(() => {
        // Cet effet se déclenchera chaque fois que showArchived changera.
        handlePageChange(1, searchTerm, filterBy);
        // eslint-disable-next-line
    }, [showArchived]);

    useEffect(() => {
        apiService.getElement('/employee/categories')
            .then(res => {
                setCategories(res.data.categories || []);
                const options = res.data.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name,
                }));
                setCategoryOptions(options);
                console.log("Categories fetched:", res.data.categories);
            });
        // refresh departments/assignments initial fetch already present below (kept)
        apiService.getElement('/employee/departments')
            .then(res => {
                const depts = res.data.departments || [];
                setDepartments(depts);
                const opts = depts.map(d => ({ value: d.id, label: d.name }));
                setDepartmentOptions(opts);
            })
            .catch(() => {});

        apiService.getElement('/employee/assignments')
            .then(res => {
                const assigns = res.data.assignments || [];
                setAssignments(assigns);
                const opts = assigns.map(a => ({ value: a.id, label: a.name }));
                setAssignmentOptions(opts);
            })
            .catch(() => {});

    }, []);
    
    const handleCloseDepartmentDrawer = () => {
        setShowDepartmentDrawer(false);
        // refresh departments used by CreatableSelect
        apiService.getElement('/employee/departments')
            .then(res => {
                const depts = res.data.departments || [];
                setDepartments(depts);
                const opts = depts.map(d => ({ value: d.id, label: d.name }));
                setDepartmentOptions(opts);
            })
            .catch(() => {});
    };

    const handleCloseAssignmentDrawer = () => {
        setShowAssignmentDrawer(false);
        apiService.getElement('/employee/assignments')
            .then(res => {
                const assigns = res.data.assignments || [];
                setAssignments(assigns);
                const opts = assigns.map(a => ({ value: a.id, label: a.name }));
                setAssignmentOptions(opts);
            })
            .catch(() => {});
    };

    const handleSelectedRowsChange = (ids) => {
        setSelectedIds(ids);
    };

    const handleCloseCategoryDrawer = () => {
        setShowCategoryDrawer(false);
        fetchEmployees(1, "", "");
    };

    const handlePageChange = (page, search, filter) => {
        const newSearchTerm = search !== undefined ? search : searchTerm;
        const newFilterBy = filter !== undefined ? filter : filterBy;
    
        setSearchTerm(newSearchTerm);
        setFilterBy(newFilterBy);
    
        // Met à jour l’URL
        const params = {};
        if (page > 1) params.page = page;
        if (newSearchTerm) params.search = newSearchTerm;
        if (newFilterBy && Object.keys(newFilterBy).length > 0) {
            params.filter_by = JSON.stringify(newFilterBy);
        }
        setSearchParams(params);

        fetchEmployees(page, newSearchTerm, newFilterBy);
    };

    const etiquetteButtonProps = {
        label: "Etiquette",
        className: "btn btn-outline btn-sm hover:bg-[--color-orange]",
        onClick: () => setShowModal(true),
    };
    return (
        <div>
            <div className="">
                <HeaderCard
                    title="Mes Collaborateurs"
                    text="Gérez les employés de votre entreprise en toute simplicité."
                    ajout="Collaborateur"
                    path="/employees/new"
                    exportProps={{
                        model_name: "employee",
                        filename: "employes_export",
                        importUrl: "/import/employee",
                        onImportSuccess: () => fetchEmployees(pagination.page, searchTerm, filterBy)
                    }}
                    dropdownActions={[
                        {
                            label: "Listes des Contrats",
                            onClick: () => window.location.href = "/contracts",
                            icon: <RiFileList3Fill />
                        },
                        {
                            label: "Gérer les étiquettes",
                            onClick: () => setShowCategoryDrawer(true),
                            icon: <RiPriceTag3Fill />
                        },
                        {
                            label: "Gérer les départements",
                            onClick: () => setShowDepartmentDrawer(true),
                            icon: <RiBuilding2Fill />
                        },
                        {
                            label: "Gérer les Affectations",
                            onClick: () => setShowAssignmentDrawer(true),
                            icon: <RiFileList3Fill />
                        },
                        {
                            custom: (
                                <div className="form-control">
                                    <label className="label cursor-pointer">
                                        <span className="label-text">Afficher les archivés</span>
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-sm"
                                            checked={showArchived}
                                            onChange={e => {
                                                const isChecked = e.target.checked;
                                                setShowArchived(isChecked);
                                            }}                                            
                                        />
                                    </label>
                                </div>
                            )
                        }
                    ]}
                />
            </div>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title="Affecter une étiquette"
                size={500}
                footer={
                    <>
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setShowModal(false)}
                        >
                            Annuler
                        </button>
                        <button
                            className="btn btn-sm btn-success"
                            disabled={!selectedCategory}
                            onClick={handleAffectCategory}
                        >
                            Affecter
                        </button>
                    </>
                }
            >
                <CreatableSelect
                    isClearable
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    onCreateOption={handleCreateCategory}
                    placeholder="Sélectionner ou créer une étiquette"
                />
            </Modal>

            {/* New: Department modal */}
            <Modal
                isOpen={showDeptModal}
                onClose={() => setShowDeptModal(false)}
                title="Affecter un département"
                size={500}
                footer={
                    <>
                        <button className="btn btn-sm btn-outline" onClick={() => setShowDeptModal(false)}>Annuler</button>
                        <button className="btn btn-sm btn-success" disabled={!selectedDepartment} onClick={handleAffectDepartment}>Affecter</button>
                    </>
                }
            >
                <div className="form-control">
                    <label className="label">Département</label>
                    <CreatableSelect
                        isClearable
                        options={departmentOptions}
                        value={selectedDepartment}
                        onChange={setSelectedDepartment}
                        onCreateOption={handleCreateDepartment}
                        placeholder="Sélectionner ou créer un département"
                    />
                </div>
            </Modal>

            {/* New: Assignment modal */}
            <Modal
                isOpen={showAssignmentModal}
                onClose={() => setShowAssignmentModal(false)}
                title="Affectation"
                size={500}
                footer={
                    <>
                        <button className="btn btn-sm btn-outline" onClick={() => setShowAssignmentModal(false)}>Annuler</button>
                        <button className="btn btn-sm btn-success" disabled={!selectedAssignment} onClick={handleAffectAssignment}>Affecter</button>
                    </>
                }
            >
                <div className="form-control">
                    <label className="label">Affectation</label>
                    <CreatableSelect
                        isClearable
                        options={assignmentOptions}
                        value={selectedAssignment}
                        onChange={setSelectedAssignment}
                        onCreateOption={handleCreateAssignment}
                        placeholder="Sélectionner ou créer une assignation"
                    />
                </div>
            </Modal>

            <Modal
                isOpen={showArchiveConfirmModal}
                onClose={() => setShowArchiveConfirmModal(false)}
                title="Confirmation"
                footer={
                    <>
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setShowArchiveConfirmModal(false)}
                        >
                            Annuler
                        </button>
                        <button
                            className={`btn btn-sm ${isArchiving ? 'btn-error' : 'btn-success'}`}
                            onClick={confirmArchive}
                        >
                            Confirmer
                        </button>
                    </>
                }
            >
                <p>Êtes-vous sûr de vouloir {isArchiving ? 'archiver' : 'désarchiver'} les employés sélectionnés ?</p>
                <p className="text-sm text-yellow-600 mt-2">
                    {isArchiving 
                        ? "Attention : Cette action archivera également les contrats associés."
                        : "Cette action réactivera également les contrats associés."}
                </p>
            </Modal>

            <div className="">
                    <TableList
                        loading={loading}
                        columns={columns}
                        onSort={handleSort}
                        sortBy={sortBy}
                        selectable={true}
                        model={"employee"}
                        data={data}
                        path="employees"
                        pagination={pagination}
                        searchQuery={searchTerm}
                        setSearchQuery={setSearchTerm}
                        filterBy={filterBy}
                        setFilterBy={setFilterBy}
                        onPageChange={(page, search, filter) => handlePageChange(page, search, filter)}
                        onSelectedRowsChange={handleSelectedRowsChange} 
                        selectActions={() => (
                            <div className="flex gap-2">                                
                                {(! showArchived) ? (
                                <>
                                    <button
                                        className={etiquetteButtonProps.className}
                                        onClick={etiquetteButtonProps.onClick}
                                        disabled={etiquetteButtonProps.disabled || selectedIds.length === 0}
                                    >
                                        {etiquetteButtonProps.label}
                                    </button>
                                    {/* <button
                                        className="btn btn-sm btn-outline hover:bg-[--color-orange]"
                                        onClick={() => setShowDeptModal(true)}
                                        disabled={selectedIds.length === 0}
                                    >
                                        Affecter Département
                                    </button> */}
                                    <button
                                        className="btn btn-sm btn-outline hover:bg-[--color-orange]"
                                        onClick={() => setShowAssignmentModal(true)}
                                        disabled={selectedIds.length === 0}
                                    >
                                        Affectation
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => handleArchive(true)}
                                        disabled={selectedIds.length === 0}
                                    >
                                        <FaArchive className="mr-2" /> Archiver
                                    </button>                                    
                                </>
                                ):(
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => handleArchive(false)}
                                        disabled={selectedIds.length === 0}
                                    >
                                        <FaArchive className="mr-2" /> Désarchiver
                                    </button>
                                )}
                            </div>
                        )}
                    />
            </div>
            <Drawer 
                isOpen={showCategoryDrawer}
                setIsOpen={open => {
                    if (!open) handleCloseCategoryDrawer();
                    else setShowCategoryDrawer(true);
                }}
                width={420}
                >
                <CategoryManager
                    categories={categories}
                    setCategories={setCategories}
                    onClose={handleCloseCategoryDrawer}
                />
            </Drawer>
            <Drawer 
                isOpen={showDepartmentDrawer}
                setIsOpen={open => {
                    if (!open) handleCloseDepartmentDrawer();
                    else setShowDepartmentDrawer(true);
                }}
                width={420}
            >
                <DepartmentManager
                    departments={departments}
                    setDepartments={setDepartments}
                    setDepartmentOptions={setDepartmentOptions}
                    onClose={handleCloseDepartmentDrawer}
                    showColumn={showDepartmentColumn}
                    setShowColumn={setShowDepartmentColumn}
                />
            </Drawer>
            <Drawer 
                isOpen={showAssignmentDrawer}
                setIsOpen={open => {
                    if (!open) handleCloseAssignmentDrawer();
                    else setShowAssignmentDrawer(true);
                }}
                width={420}
            >
                <AssignmentManager
                    assignments={assignments}
                    setAssignments={setAssignments}
                    setAssignmentOptions={setAssignmentOptions}
                    onClose={handleCloseAssignmentDrawer}
                    showColumn={showAssignmentColumn}
                    setShowColumn={setShowAssignmentColumn}
                />
            </Drawer>
        </div>
    );
}

export default Employees;